import type { CardForm, RoundKey } from "@vocifuchs/shared";
import { api } from "./api.js";

/**
 * What the app keeps on the device to practise without a connection (issue
 * #120, docs/UX.md §4 «Offline»): the rounds Start offers (`roundsToKeep`)
 * and what Start showed last. Only for the child logged in on this device, in
 * `localStorage`; gone on logout, account deletion and an ended session
 * (session.tsx). The answers of a round played offline go through the
 * outbox (outbox.ts).
 *
 * A kept round can be a little out of date: a word may no longer be due.
 * That does no harm, the worker schedules from the state it has when the
 * round arrives, and a right answer before the due day lifts nothing (#96).
 */

export interface PracticeCard {
  term_id: string;
  direction: "ab" | "ba";
  prompt: string;
  solution: string;
  /** The form the box picks (issue #63); «Kacheln zeigen» can only make it easier for one card. */
  mode: CardForm;
  choices: string[];
  lang_prompt: string;
  lang_answer: string;
}

/** What POST /api/practice/session answers. */
export interface SessionReply {
  title: string | null;
  lang_b: "en" | "fr";
  extra?: boolean;
  cards: PracticeCard[];
}

/** The request for a round that can be kept; «Wiederholen» cannot. */
export function sessionBody(key: RoundKey) {
  return key.startsWith("due:")
    ? { mode: "due", lang: key.slice(4), size: 10 }
    : { list_id: key.slice(5), mode: "exam", size: 10 };
}

const ROUNDS = "vf_rounds";
const START = "vf_start";
/** Start fetches a kept round again after this long, or right after a round was sent. */
const FRESH_MS = 10 * 60_000;

interface Kept {
  user_id: string;
  rounds: Partial<Record<RoundKey, { kept_at: number; reply: SessionReply }>>;
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Full or refused: the app then practises online only.
  }
}

function keptFor(userId: string): Kept {
  const kept = read<Kept>(ROUNDS);
  return kept?.user_id === userId ? kept : { user_id: userId, rounds: {} };
}

export function keepRoundCards(userId: string, key: RoundKey, reply: SessionReply): void {
  const kept = keptFor(userId);
  kept.rounds[key] = { kept_at: Date.now(), reply };
  write(ROUNDS, kept);
}

export function keptRoundCards(userId: string, key: RoundKey): SessionReply | null {
  return keptFor(userId).rounds[key]?.reply ?? null;
}

/** After a round arrived the boxes moved: Start fetches every kept round again, the old ones stay until then. */
export function roundsChanged(): void {
  const kept = read<Kept>(ROUNDS);
  if (!kept) return;
  for (const entry of Object.values(kept.rounds)) if (entry) entry.kept_at = 0;
  write(ROUNDS, kept);
}

/**
 * Fetches the rounds Start offers that are missing or older than FRESH_MS,
 * one after the other, and drops kept rounds Start no longer offers. Quietly:
 * without a connection the old ones stay.
 */
export function keepRounds(userId: string, keys: RoundKey[]): Promise<void> {
  // One at a time: two visits to Start in quick succession fetch once.
  keeping ??= fetchRounds(userId, keys).finally(() => {
    keeping = null;
  });
  return keeping;
}

let keeping: Promise<void> | null = null;

async function fetchRounds(userId: string, keys: RoundKey[]): Promise<void> {
  const kept = keptFor(userId);
  for (const key of Object.keys(kept.rounds) as RoundKey[]) if (!keys.includes(key)) delete kept.rounds[key];
  write(ROUNDS, kept);
  for (const key of keys) {
    const entry = keptFor(userId).rounds[key];
    if (entry && Date.now() - entry.kept_at < FRESH_MS) continue;
    try {
      const reply = await api<SessionReply>("/api/practice/session", {
        method: "POST",
        body: JSON.stringify(sessionBody(key)),
      });
      keepRoundCards(userId, key, reply);
    } catch {
      return;
    }
  }
}

/** Start as it was last shown with a connection. */
export function keepStart<T>(userId: string, start: T): void {
  write(START, { user_id: userId, start });
}

export function keptStart<T>(userId: string): T | null {
  const kept = read<{ user_id: string; start: T }>(START);
  return kept?.user_id === userId ? kept.start : null;
}

export function forgetOffline(): void {
  try {
    localStorage.removeItem(ROUNDS);
    localStorage.removeItem(START);
  } catch {
    // Nothing kept, nothing to forget.
  }
}
